import React, { useRef, useState, useEffect } from "react";
import { Box, Button, Typography, CircularProgress, Stack, Alert } from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import ModalComponent from "./Modal";

const BulkLoadModal = ({
  open,
  onClose,
  onSelectFile,
  title = "Carga masiva",
  description = "Descargue la plantilla, complétela y súbala desde su equipo.",
  template,
  accept = ".xlsx",
  maxSizeMB = 10,
  uploadButtonLabel = "Subir archivo",
  icon = null,
  warnings = [],
  closeOnSuccess = true,
  extraActions = null,
  isLoading = false,
}) => {
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const isCurrentlyLoading = isLoading || loading;

  useEffect(() => {
    if (!open) {
      setLoading(false);
      setErrorMsg("");
    }
  }, [open]);

  useEffect(() => {
    if (!isLoading && loading) {
      setLoading(false);
    }
  }, [isLoading, loading]);

  const parseAllowedExtensions = () => {
    if (!accept) return [];
    return accept
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)
      .map((s) => (s.startsWith(".") ? s.slice(1) : s))
      .map((s) => s.toLowerCase());
  };

  const isExtensionAllowed = (filename) => {
    const allowed = parseAllowedExtensions();
    if (allowed.length === 0) return true;
    const ext = (filename.split(".").pop() || "").toLowerCase();
    return allowed.includes(ext);
  };

  const isSizeAllowed = (sizeBytes) => {
    if (!maxSizeMB || maxSizeMB <= 0) return true;
    const maxBytes = maxSizeMB * 1024 * 1024;
    return sizeBytes <= maxBytes;
  };

  const handleTemplateDownload = () => {
    if (template?.onClick) {
      template.onClick();
      return;
    }
    if (template?.url) {
      window.open(template.url, "_blank", "noopener,noreferrer");
    }
  };

  const resetInput = () => {
    if (fileInputRef.current) fileInputRef.current.value = null;
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!isExtensionAllowed(file.name)) {
      setErrorMsg(
        `Formato no permitido. Tipos válidos: ${parseAllowedExtensions()
          .map((x) => "." + x)
          .join(", ")}`,
      );
      resetInput();
      return;
    }

    if (!isSizeAllowed(file.size)) {
      setErrorMsg(`El archivo supera ${maxSizeMB}MB.`);
      resetInput();
      return;
    }

    setErrorMsg("");
    setLoading(true);
    try {
      await onSelectFile(file);
    } catch (err) {
      setErrorMsg((err && (err.message || err.toString())) || "Ocurrió un error al procesar el archivo.");
      setLoading(false);
    }
    resetInput();
  };

  return (
    <ModalComponent open={open} handleClose={onClose} width="600px">
      <Box sx={{ p: 4 }}>
        {/* Encabezado */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          {icon}
          <Typography variant="h6" fontWeight={600} sx={{ lineHeight: 1.2 }}>
            {title}
          </Typography>
        </Box>

        {/* Descripción */}
        {description ? (
          <Typography variant="body1" color="text.secondary" mb={3}>
            {description}
          </Typography>
        ) : null}

        {/* Acciones principales */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          {template ? (
            <Button variant="outlined" onClick={handleTemplateDownload} disabled={!template?.onClick && !template?.url} sx={{ minHeight: 40 }}>
              {template?.label || "Descargar plantilla"}
            </Button>
          ) : null}

          <Button
            variant="contained"
            startIcon={isCurrentlyLoading ? <CircularProgress size={18} color="inherit" /> : <UploadFileIcon />}
            disabled={isCurrentlyLoading}
            onClick={() => fileInputRef.current?.click()}
            sx={{ minHeight: 40 }}
          >
            {isCurrentlyLoading ? "Cargando..." : uploadButtonLabel}
          </Button>

          {extraActions}

          <input type="file" hidden accept={accept} ref={fileInputRef} onChange={handleFileChange} />
        </Stack>

        {/* Error de validación o backend */}
        {errorMsg ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMsg}
          </Alert>
        ) : null}

        {/* Advertencias */}
        {Array.isArray(warnings) && warnings.length > 0 ? (
          <Box sx={{ mt: 1 }}>
            {warnings.map((w, idx) => (
              <Typography key={idx} variant="caption" color="warning.main" display="block" sx={{ mb: 0.5 }}>
                {typeof w === "string" ? w : w}
              </Typography>
            ))}
          </Box>
        ) : null}
      </Box>
    </ModalComponent>
  );
};

export default BulkLoadModal;
